import {CheckIcon, SearchIcon, TriangleDownIcon, TriangleUpIcon} from '@primer/octicons-react'
import {FocusKeys, focusZone} from '@primer/behaviors'
import {clsx} from 'clsx'
import {
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {t} from '../../../../../lib/utils/localization'
import styles from './SearchableSelector.module.css'

type FocusTarget = 'next' | 'trigger' | null

const OPEN_KEYS = new Set(['ArrowDown', 'ArrowUp', ' ', 'Enter'])

function getDefaultResultsAnnouncement(resultCount: number, noResultsMessage: string) {
  if (resultCount === 0) {
    return noResultsMessage
  }

  const template = resultCount === 1 ? t('{count} result available') : t('{count} results available')
  return template.replace('{count}', String(resultCount))
}

export type SearchableSelectorProps<Item> = {
  id: string
  items: Item[]
  selectedItemKey?: string
  getItemKey: (item: Item) => string
  filterItems: (items: Item[], query: string) => Item[]
  onSelect: (item: Item) => void
  /** Must return phrasing content because it is rendered inside a button. */
  renderTriggerContent: (selectedItem: Item | undefined) => ReactNode
  getTriggerAriaLabel: (selectedItem: Item | undefined) => string
  renderItem: (args: {item: Item; isHighlighted: boolean; isSelected: boolean}) => ReactNode
  searchInputAriaLabel: string
  searchInputPlaceholder: string
  listboxAriaLabel: string
  noResultsMessage: string
  getResultsAnnouncement?: (resultCount: number) => string
  nextFocusRef?: RefObject<HTMLElement | null>
  triggerClassName?: string
}

export function SearchableSelector<Item>({
  id,
  items,
  selectedItemKey,
  getItemKey,
  filterItems,
  onSelect,
  renderTriggerContent,
  getTriggerAriaLabel,
  renderItem,
  searchInputAriaLabel,
  searchInputPlaceholder,
  listboxAriaLabel,
  noResultsMessage,
  getResultsAnnouncement,
  nextFocusRef,
  triggerClassName,
}: SearchableSelectorProps<Item>) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [highlightedIndex, setHighlightedIndex] = useState(0)

  const defaultFilteredItems = useMemo(() => filterItems(items, ''), [filterItems, items])
  const filteredItems = useMemo(
    () => (searchQuery === '' ? defaultFilteredItems : filterItems(items, searchQuery)),
    [defaultFilteredItems, filterItems, items, searchQuery],
  )
  const selectedItem = useMemo(
    () => items.find(item => getItemKey(item) === selectedItemKey),
    [getItemKey, items, selectedItemKey],
  )
  const selectedItemIndex = useMemo(
    () => defaultFilteredItems.findIndex(item => getItemKey(item) === selectedItemKey),
    [defaultFilteredItems, getItemKey, selectedItemKey],
  )
  const resultsAnnouncement = useMemo(
    () =>
      getResultsAnnouncement?.(filteredItems.length) ??
      getDefaultResultsAnnouncement(filteredItems.length, noResultsMessage),
    [filteredItems.length, getResultsAnnouncement, noResultsMessage],
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const pendingFocusTargetRef = useRef<FocusTarget>(null)

  const listboxId = `${id}-listbox`
  const clampedHighlightedIndex = filteredItems.length === 0 ? 0 : Math.min(highlightedIndex, filteredItems.length - 1)
  const highlightedItem = filteredItems[clampedHighlightedIndex]
  const highlightedOptionId = highlightedItem ? `${id}-option-${clampedHighlightedIndex}` : undefined

  const openDropdown = useCallback((initialHighlightedIndex = 0) => {
    setIsOpen(true)
    setSearchQuery('')
    setHighlightedIndex(initialHighlightedIndex)
  }, [])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
  }, [])

  const openDropdownAtSelectedItem = useCallback(() => {
    openDropdown(selectedItemIndex >= 0 ? selectedItemIndex : 0)
  }, [openDropdown, selectedItemIndex])

  const toggleDropdownAtSelectedItem = useCallback(() => {
    if (isOpen) {
      closeDropdown()
      return
    }

    openDropdownAtSelectedItem()
  }, [closeDropdown, isOpen, openDropdownAtSelectedItem])

  const closeDropdownWithFocus = useCallback(
    (focusTarget: FocusTarget = null) => {
      pendingFocusTargetRef.current = focusTarget
      closeDropdown()
    },
    [closeDropdown],
  )

  const selectItemAndClose = useCallback(
    (item: Item) => {
      onSelect(item)
      closeDropdownWithFocus('trigger')
    },
    [closeDropdownWithFocus, onSelect],
  )

  const selectHighlightedItem = useCallback(() => {
    const item = filteredItems[clampedHighlightedIndex]
    if (!item) {
      return
    }

    selectItemAndClose(item)
  }, [clampedHighlightedIndex, filteredItems, selectItemAndClose])

  useEffect(() => {
    const listElement = listRef.current
    const searchInputElement = searchInputRef.current

    if (!(listElement instanceof HTMLElement) || !(searchInputElement instanceof HTMLElement)) {
      return
    }

    if (!isOpen || filteredItems.length === 0) {
      return
    }

    const handleActiveDescendantChanged = (current: HTMLElement | undefined) => {
      if (!current || current.getAttribute('role') !== 'option') {
        return
      }

      const currentIndex = current.getAttribute('data-index')
      if (currentIndex === null) {
        return
      }

      const nextIndex = Number(currentIndex)
      if (Number.isNaN(nextIndex)) {
        return
      }

      setHighlightedIndex(nextIndex)
      current.scrollIntoView({block: 'nearest'})
    }

    const abortController = focusZone(listElement, {
      activeDescendantControl: searchInputElement,
      bindKeys: FocusKeys.ArrowVertical | FocusKeys.HomeAndEnd | FocusKeys.PageUpDown,
      focusInStrategy: () => listElement.querySelector<HTMLElement>('[data-highlighted="true"]') ?? undefined,
      focusOutBehavior: 'wrap',
      focusableElementFilter: element => element.getAttribute('role') === 'option',
      onActiveDescendantChanged: handleActiveDescendantChanged,
    })

    return () => abortController.abort()
  }, [filteredItems.length, isOpen])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) {
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- opening a custom combobox requires explicit key handling
        if (OPEN_KEYS.has(event.key)) {
          event.preventDefault()
          openDropdownAtSelectedItem()
        }
        return
      }

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- selection and dismissal keys are custom combobox behavior
      switch (event.key) {
        case 'Enter':
          if (filteredItems.length === 0) {
            event.preventDefault()
            return
          }

          event.preventDefault()
          selectHighlightedItem()
          break
        case 'Escape':
          event.preventDefault()
          closeDropdownWithFocus('trigger')
          break
        case 'Tab':
          if (!event.shiftKey && nextFocusRef) {
            event.preventDefault()
            closeDropdownWithFocus('next')
            break
          }

          closeDropdown()
          break
      }
    },
    [
      closeDropdown,
      closeDropdownWithFocus,
      filteredItems.length,
      isOpen,
      nextFocusRef,
      openDropdownAtSelectedItem,
      selectHighlightedItem,
    ],
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [closeDropdown, isOpen])

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus()
      return
    }

    const pendingFocusTarget = pendingFocusTargetRef.current
    if (!pendingFocusTarget) {
      return
    }

    pendingFocusTargetRef.current = null

    const focusFrameId = requestAnimationFrame(() => {
      if (pendingFocusTarget === 'next') {
        nextFocusRef?.current?.focus()
        return
      }

      triggerRef.current?.focus()
    })

    return () => cancelAnimationFrame(focusFrameId)
  }, [isOpen, nextFocusRef])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const scrollFrameId = requestAnimationFrame(() => {
      const highlightedOption = listRef.current?.querySelector<HTMLElement>(`[data-index="${clampedHighlightedIndex}"]`)
      highlightedOption?.scrollIntoView({block: 'nearest'})
    })

    return () => cancelAnimationFrame(scrollFrameId)
  }, [clampedHighlightedIndex, isOpen])

  return (
    <div ref={containerRef} className={styles.root}>
      <button
        ref={triggerRef}
        type="button"
        className={clsx(styles.trigger, triggerClassName)}
        onClick={toggleDropdownAtSelectedItem}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={getTriggerAriaLabel(selectedItem)}
      >
        <span className={styles.triggerContent}>{renderTriggerContent(selectedItem)}</span>
        <span className={styles.selectIndicator} aria-hidden="true">
          <TriangleUpIcon className={styles.selectIndicatorIcon} size={10} />
          <TriangleDownIcon className={styles.selectIndicatorIcon} size={10} />
        </span>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrapper}>
            <div className={styles.searchField}>
              <input
                ref={searchInputRef}
                type="text"
                role="combobox"
                className={styles.searchInput}
                value={searchQuery}
                onChange={event => {
                  setSearchQuery(event.target.value)
                  setHighlightedIndex(0)
                }}
                onKeyDown={handleKeyDown}
                placeholder={searchInputPlaceholder}
                aria-label={searchInputAriaLabel}
                aria-haspopup="listbox"
                aria-controls={listboxId}
                aria-autocomplete="list"
                aria-expanded={isOpen}
                aria-activedescendant={highlightedOptionId}
              />
              <SearchIcon className={styles.searchIcon} size={16} aria-hidden="true" />
            </div>
          </div>

          <div className={styles.statusMessage} role="status" aria-live="polite" aria-atomic="true">
            {resultsAnnouncement}
          </div>

          <ul ref={listRef} id={listboxId} className={styles.list} role="listbox" aria-label={listboxAriaLabel}>
            {filteredItems.length === 0 ? (
              <li className={styles.noResults} aria-hidden="true">
                {noResultsMessage}
              </li>
            ) : (
              filteredItems.map((item, index) => {
                const itemKey = getItemKey(item)
                const isSelected = itemKey === selectedItemKey
                const isHighlighted = index === clampedHighlightedIndex

                return (
                  <li
                    key={itemKey}
                    data-index={index}
                    id={`${id}-option-${index}`}
                    role="option"
                    aria-selected={isSelected}
                    data-selected={isSelected}
                    data-highlighted={isHighlighted}
                    className={clsx(
                      styles.option,
                      isSelected && styles.optionSelected,
                      isHighlighted && styles.optionHighlighted,
                    )}
                    onPointerDown={event => {
                      if (event.button !== 0) {
                        return
                      }

                      event.preventDefault()
                      setHighlightedIndex(index)
                    }}
                    onClick={() => {
                      selectItemAndClose(item)
                    }}
                    onKeyDown={event => {
                      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- fallback option activation; combobox navigation stays on the input/focusZone
                      if (event.key !== 'Enter' && event.key !== ' ') {
                        return
                      }

                      event.preventDefault()
                      selectItemAndClose(item)
                    }}
                    tabIndex={-1}
                  >
                    <div className={styles.optionContent}>{renderItem({item, isHighlighted, isSelected})}</div>
                    <span className={styles.optionCheck} aria-hidden="true">
                      {isSelected && <CheckIcon size={16} />}
                    </span>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
